import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Link, useParams } from "react-router-dom";
import axiosInstance, { weburl } from "../../../axiosInstance";
import Sidebar from "../../components/Sidebar";
import Navbar from "../../components/Navbar";
import LoadingScreen from "../../components/LoadingScreen";
import LocationPicker from "../../../components/LocationPicker";

const initialForm = {
  username: "",
  email: "",
  phone: "",
  Gender: "1",
  gender: "1",
  state: "",
  statename: "",
  country: "",
  city: "",
  pincode: "",
  DOB: "",
  latitude: "",
  longitude: "",
  password: "",
  confirm_password: "",
  profile_url: "",
  address: "",
  status: "1",
  MedicalRegistrationNumber: "",
  Doc2: "",
  Doc3: "",
};

const capitalizeEachWord = (str) => {
  if (!str) return str;
  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const createSlug = (str = "") =>
  str
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");

const UserView = () => {
  const { slug } = useParams();
  const [inputs, setInputs] = useState(initialForm);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    let nextValue = value;

    if (name === "state") {
      const selectedState = zones.find((state) => state._id === value);
      setInputs((prev) => ({
        ...prev,
        state: value,
        statename: selectedState?.name || "",
        city: "",
      }));
      return;
    }

    if (name === "username") {
      nextValue = capitalizeEachWord(value);
      setInputs((prev) => ({
        ...prev,
        username: nextValue,
        profile_url: createSlug(nextValue),
      }));
      return;
    }

    if (name === "profile_url") {
      nextValue = createSlug(value);
    }

    if (name === "Gender") {
      setInputs((prev) => ({
        ...prev,
        Gender: nextValue,
        gender: nextValue,
      }));
      return;
    }

    setInputs((prev) => ({ ...prev, [name]: nextValue }));
  };

  const handleFileChange = (event) => {
    const { name, files } = event.target;
    setInputs((prev) => ({ ...prev, [name]: files?.[0] || "" }));
  };

  const buildSubmitData = () => {
    const formData = new FormData();

    Object.entries(inputs).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return;
      }

      if (value instanceof File) {
        formData.append(key, value);
      } else {
        formData.append(key, value);
      }
    });

    return formData;
  };

  const getDocumentPreview = (value) => {
    if (!value) {
      return "/img/imageupload.png";
    }

    if (value instanceof File) {
      return URL.createObjectURL(value);
    }

    return weburl + value;
  };

  const applyLocation = (location) => {
    setInputs((prev) => ({
      ...prev,
      ...location,
      state: location.state || prev.state,
      statename: location.statename || prev.statename,
    }));
  };

  const fetchZones = async () => {
    const response = await axiosInstance.get("/get-all-zones");
    setZones(response.data.Zones || []);
  };

  const fetchUser = useCallback(async () => {
    const response = await axiosInstance.get(`/admin/get-user/${slug}`);
    const user = response.data.User || {};

    setInputs((prev) => ({
      ...prev,
      username: user.username || "",
      email: user.email || "",
      phone: user.phone || "",
      Gender: String(user.gender ?? user.Gender ?? "1"),
      gender: String(user.gender ?? user.Gender ?? "1"),
      state: user.state || "",
      statename: user.statename || "",
      country: user.country || "",
      city: user.city || "",
      pincode: user.pincode || "",
      DOB: user.DOB ? String(user.DOB).slice(0, 10) : "",
      latitude: user.latitude || "",
      longitude: user.longitude || "",
      profile_url: user.profile_url || "",
      address: user.address || "",
      status: String(user.status ?? "1"),
      MedicalRegistrationNumber: user.MedicalRegistrationNumber || "",
      Doc2: user.Doc2 || "",
      Doc3: user.Doc3 || "",
      password: "",
      confirm_password: "",
    }));
  }, [slug]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        await Promise.all([fetchZones(), fetchUser()]);
      } catch (error) {
        console.error("Error fetching user:", error);
        toast.error(error.response?.data?.message || "Unable to load user");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [slug, fetchUser]);

  const handleSubmit = async () => {
    const requiredFields = [
      { name: "username", message: "Please enter Full Name" },
      { name: "phone", message: "Please enter phone number" },
      { name: "email", message: "Please enter email" },
    ];

    for (const field of requiredFields) {
      if (!inputs[field.name]) {
        toast.error(field.message);
        return;
      }
    }

    try {
      setSubmitLoading(true);
      const { data } = await axiosInstance.put(
        `/admin/update-user-emp/${slug}`,
        buildSubmitData(),
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (data.success) {
        toast.success("User updated successfully");
        await fetchUser();
      }
    } catch (error) {
      console.error("Error updating user:", error);
      toast.error(error.response?.data?.message || "Unable to update user");
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <>
      <Sidebar />
      <main className="content">
        <Navbar />

        <div className="py-4">
          <div className="d-flex justify-content-between w-100 flex-wrap mb-3">
            <div>
              <h1 className="h4 mb-1">Update Profile</h1>
              <p className="text-muted small mb-0">
                Edit the selected user's profile information.
              </p>
            </div>
            <Link
              to="/all-user"
              className="btn btn-primary d-inline-flex align-items-center"
            >
              Go back
            </Link>
          </div>

          <div className="container pt-0">
            <div
              className="dashboard-content px-4 py-3"
              style={{ maxWidth: "100vw" }}
            >
              {loading ? (
                <LoadingScreen />
              ) : (
                <form className="m-b20 row">
                  <div className="card border-0 bg-white p-4 mb-4">
                    <div className="row">
                      <div className="mb-4">
                        <label className="form-label text-dark" htmlFor="username">
                          Full Name <span className="text-danger">( Required )</span>
                        </label>
                        <input
                          type="text"
                          id="username"
                          className="form-control"
                          name="username"
                          value={inputs.username}
                          onChange={handleChange}
                        />
                      </div>

                      <div className="mb-4 col-6">
                        <label className="form-label text-dark" htmlFor="email">
                          Email <span className="text-danger">( Required )</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          className="form-control"
                          name="email"
                          value={inputs.email}
                          onChange={handleChange}
                        />
                      </div>

                      <div className="mb-4 col-6">
                        <label className="form-label text-dark" htmlFor="phone">
                          Phone <span className="text-danger">( Required )</span>
                        </label>
                        <input
                          type="text"
                          id="phone"
                          className="form-control"
                          name="phone"
                          value={inputs.phone}
                          onChange={handleChange}
                        />
                      </div>

                      <div className="mb-4 col-6">
                        <label className="form-label text-dark" htmlFor="Gender">
                          Gender <span className="text-danger">( Required )</span>
                        </label>
                        <select
                          className="form-control"
                          id="Gender"
                          value={inputs.Gender}
                          name="Gender"
                          onChange={handleChange}
                        >
                          <option value="">Select Gender</option>
                          <option value="1">Male</option>
                          <option value="2">Female</option>
                          <option value="0">Other</option>
                        </select>
                      </div>

                      <div className="mb-4 col-6">
                        <label className="form-label text-dark" htmlFor="DOB">
                          Date of birth <span className="text-danger">( Required )</span>
                        </label>
                        <input
                          type="date"
                          id="DOB"
                          className="form-control"
                          name="DOB"
                          value={inputs.DOB}
                          onChange={handleChange}
                        />
                      </div>

                      <div className="col-12">
                        <LocationPicker
                          value={inputs.address}
                          zones={zones}
                          onSelect={applyLocation}
                          label="Search address on map"
                        />
                      </div>

                      <div className="mb-4 col-6">
                        <label className="form-label text-dark" htmlFor="state">
                          State <span className="text-danger">( Required )</span>
                        </label>
                        <select
                          className="form-control"
                          id="state"
                          name="state"
                          value={inputs.state}
                          onChange={handleChange}
                        >
                          <option value="">
                            {zones.length ? "Select State" : "Loading..."}
                          </option>
                          {zones.map((item) => (
                            <option key={item._id} value={item._id}>
                              {item.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="mb-4 col-6">
                        <label className="form-label text-dark" htmlFor="city">
                          City <span className="text-danger">( Required )</span>
                        </label>
                        <input
                          className="form-control"
                          id="city"
                          name="city"
                          value={inputs.city}
                          onChange={handleChange}
                        />
                      </div>

                      <div className="mb-4 col-6">
                        <label className="form-label text-dark" htmlFor="pincode">
                          Pincode <span className="text-danger">( Required )</span>
                        </label>
                        <input
                          type="number"
                          id="pincode"
                          className="form-control custom-date"
                          name="pincode"
                          value={inputs.pincode}
                          onChange={handleChange}
                        />
                      </div>

                      <div className="mb-4 col-6">
                        <label className="form-label text-dark" htmlFor="country">
                          Country
                        </label>
                        <input
                          id="country"
                          className="form-control"
                          name="country"
                          value={inputs.country}
                          onChange={handleChange}
                        />
                      </div>

                      <div className="mb-4 col-md-6">
                        <label className="form-label text-dark" htmlFor="password">
                          New Password
                        </label>
                        <div className="input-group input-sm">
                          <input
                            type={showPassword ? "text" : "password"}
                            id="password"
                            className="form-control dz-password"
                            name="password"
                            value={inputs.password}
                            onChange={handleChange}
                          />
                          <span className="input-group-text text-primary p-1">
                            <i
                              className={
                                showPassword
                                  ? "ri-eye-off-fill cursor"
                                  : "ri-eye-fill cursor"
                              }
                              onClick={() => setShowPassword((value) => !value)}
                            />
                          </span>
                        </div>
                      </div>

                      <div className="mb-4 col-md-6">
                        <label
                          className="form-label text-dark"
                          htmlFor="confirm_password"
                        >
                          Confirm Password
                        </label>
                        <input
                          type={showPassword ? "text" : "password"}
                          id="confirm_password"
                          className="form-control"
                          name="confirm_password"
                          value={inputs.confirm_password}
                          onChange={handleChange}
                        />
                      </div>

                      <div className="mb-4 col-md-6">
                        <label className="form-label text-dark" htmlFor="profile_url">
                          Profile URL <span className="text-danger">( Required )</span>
                        </label>
                        <input
                          type="text"
                          id="profile_url"
                          className="form-control custom-date"
                          name="profile_url"
                          value={inputs.profile_url}
                          onChange={handleChange}
                        />
                      </div>

                      <div className="mb-4 col-md-6">
                        <label
                          className="form-label text-dark"
                          htmlFor="MedicalRegistrationNumber"
                        >
                          Professional Registration ID
                        </label>
                        <input
                          type="text"
                          id="MedicalRegistrationNumber"
                          className="form-control custom-date"
                          name="MedicalRegistrationNumber"
                          value={inputs.MedicalRegistrationNumber}
                          readOnly
                        />
                      </div>

                      <div className="mb-4 col-md-6">
                        <label className="form-label text-dark" htmlFor="status">
                          Status
                        </label>
                        <select
                          className="form-control"
                          id="status"
                          name="status"
                          value={inputs.status}
                          onChange={handleChange}
                        >
                          <option value="1">Active</option>
                          <option value="0">Inactive</option>
                        </select>
                      </div>

                      <div className="mb-4 col-md-6">
                        <label className="form-label text-dark" htmlFor="Doc2">
                          Aadhaar card (Front)
                        </label>
                        <div className="openimage-container">
                          {inputs.Doc2 && typeof inputs.Doc2 === "string" && inputs.Doc2.indexOf(".pdf") !== -1 ? (
                            <embed
                              src={getDocumentPreview(inputs.Doc2)}
                              id="Doc2Preview"
                              alt="Aadhaar card front"
                              style={{
                                width: "100%",
                                aspectRatio: "1/1",
                                background: "#f8f9fa",
                                display: "block",
                                borderRadius: 5,
                                objectFit: "contain",
                                border: "1px solid grey",
                              }}
                            />
                          ) : (
                            <img
                              src={getDocumentPreview(inputs.Doc2)}
                              id="Doc2Preview"
                              alt="Aadhaar card front"
                              style={{
                                width: "100%",
                                aspectRatio: "1/1",
                                background: "#f8f9fa",
                                display: "block",
                                borderRadius: 5,
                                objectFit: "contain",
                                border: "1px solid grey",
                              }}
                            />
                          )}
                          <input
                            type="file"
                            id="Doc2"
                            className="form-control mt-2"
                            name="Doc2"
                            accept=".jpg, .jpeg, .png, .pdf, .webp"
                            onChange={handleFileChange}
                          />
                        </div>
                      </div>

                      <div className="mb-4 col-md-6">
                        <label className="form-label text-dark" htmlFor="Doc3">
                          Aadhaar card (Back)
                        </label>
                        <div className="openimage-container">
                          {inputs.Doc3 && typeof inputs.Doc3 === "string" && inputs.Doc3.indexOf(".pdf") !== -1 ? (
                            <embed
                              src={getDocumentPreview(inputs.Doc3)}
                              id="Doc3Preview"
                              alt="Aadhaar card back"
                              style={{
                                width: "100%",
                                aspectRatio: "1/1",
                                background: "#f8f9fa",
                                display: "block",
                                borderRadius: 5,
                                objectFit: "contain",
                                border: "1px solid grey",
                              }}
                            />
                          ) : (
                            <img
                              src={getDocumentPreview(inputs.Doc3)}
                              id="Doc3Preview"
                              alt="Aadhaar card back"
                              style={{
                                width: "100%",
                                aspectRatio: "1/1",
                                background: "#f8f9fa",
                                display: "block",
                                borderRadius: 5,
                                objectFit: "contain",
                                border: "1px solid grey",
                              }}
                            />
                          )}
                          <input
                            type="file"
                            id="Doc3"
                            className="form-control mt-2"
                            name="Doc3"
                            accept=".jpg, .jpeg, .png, .pdf, .webp"
                            onChange={handleFileChange}
                          />
                        </div>
                      </div>

                      <div className="mb-4 col-12">
                        <label className="form-label text-dark" htmlFor="address">
                          Address <span className="text-danger">( Required )</span>
                        </label>
                        <textarea
                          id="address"
                          className="form-control custom-date"
                          name="address"
                          value={inputs.address}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="col-md-12 text-end">
                    <button
                      className="btn btn-gray-800 d-inline-flex align-items-center justify-content-center mb-4 ms-auto"
                      type="button"
                      onClick={handleSubmit}
                      disabled={submitLoading}
                    >
                      {submitLoading ? "Loading..." : "Update Profile"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default UserView;
